/**
 * InductionInviteManager - Phase 3 Integration
 * Staff dashboard for managing contractor/visitor induction invites
 * Uses Phase 3 RPC functions and email automation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@shared/config/routes';
import { Card, CardContent } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import {
  Plus,
  Mail,
  Building2,
  Calendar,
  Clock,
  Send,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Search,
  Phone,
  RefreshCw,
  Trash2,
  Shield,
} from 'lucide-react';
import { useAuth } from '@shared/contexts/AuthContext';
import { useToast } from '@shared/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import CreateInviteModal from './CreateInviteModal';
import { listInvites, resendInvite, deleteInvite } from '@shared/services/staffInviteService';
import { supabase } from '@shared/supabaseClient';

const InductionInviteManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [invites, setInvites] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [resendingRef, setResendingRef] = useState(null);
  const [deletingRef, setDeletingRef] = useState(null);
  const [reviewingInvite, setReviewingInvite] = useState(null);
  const [reviewSubmission, setReviewSubmission] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    loadDepots();
    loadInvites();
  }, [statusFilter]);

  const loadDepots = async () => {
    try {
      const { data, error } = await supabase
        .from('depots')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setDepots(data || []);
    } catch (error) {
      console.error('Error loading depots:', error);
      toast({
        title: 'Warning',
        description: 'Failed to load depot list',
        variant: 'destructive',
      });
    }
  };

  const loadInvites = async () => {
    setLoading(true);
    try {
      // Admins/owners see all depots; depot managers see only their depot
      const isAdmin = ['admin', 'owner', 'system-admin'].includes(user?.role);
      const depotFilter = isAdmin ? null : (user?.depot_id || null);

      const result = await listInvites({
        status: statusFilter,
        depot_id: depotFilter,
        limit: 100,
      });

      if (result.success) {
        // Exclude project team member submissions (TM-* prefix) —
        // these are reviewed at the project level, not individually.
        const standalone = (result.invites || []).filter(
          inv => !inv.public_reference?.startsWith('TM-')
        );
        setInvites(standalone);
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to load invites',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Load invites error:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invites',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (publicReference) => {
    setResendingRef(publicReference);
    try {
      const result = await resendInvite(publicReference);

      if (result.success) {
        toast({
          title: 'Invite Resent',
          description: `New invite email sent. Expires: ${new Date(result.expires_at).toLocaleString()}`,
        });
        await loadInvites();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to resend invite',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Resend error:', error);
      toast({
        title: 'Error',
        description: 'Failed to resend invite',
        variant: 'destructive',
      });
    } finally {
      setResendingRef(null);
    }
  };

  const handleDelete = async (invite) => {
    const isApproved = invite.status === 'approved';
    const confirmMsg = isApproved
      ? `Delete approved invite for ${invite.person_name} (${invite.public_reference})?\n\n⚠️ This will also REVOKE their active site pass.\n\nThis cannot be undone.`
      : `Delete invite for ${invite.person_name} (${invite.public_reference})?\n\nThis cannot be undone.`;
    const confirmed = window.confirm(confirmMsg);
    if (!confirmed) return;

    setDeletingRef(invite.public_reference);
    try {
      const result = await deleteInvite(invite.public_reference);

      if (result.success) {
        const desc = result.pass_revoked
          ? `Invite ${invite.public_reference} for ${invite.person_name} deleted and site pass revoked.`
          : `Invite ${invite.public_reference} for ${invite.person_name} has been deleted.`;
        toast({ title: 'Invite Deleted', description: desc });
        await loadInvites();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to delete invite',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete invite',
        variant: 'destructive',
      });
    } finally {
      setDeletingRef(null);
    }
  };

  const openReview = async (invite) => {
    setReviewingInvite(invite);
    setReviewSubmission(null);
    setRejectionReason('');
    setShowRejectForm(false);
    setReviewLoading(true);
    try {
      const { data, error } = await supabase
        .from('induction_submissions')
        .select('id, answers, nature_of_work, work_area, requesting_person, submitted_at')
        .eq('public_reference', invite.public_reference)
        .maybeSingle();
      if (!error && data) setReviewSubmission(data);
    } catch (e) {
      console.error('Error loading submission details', e);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleApproveInvite = async () => {
    if (!reviewingInvite) return;
    setApproving(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-approve-induction', {
        body: {
          public_reference: reviewingInvite.public_reference,
          approve: true,
          approved_by: user?.id,
        },
      });
      // supabase-js wraps non-2xx responses in FunctionsHttpError — the real
      // error payload is in error.context which is a raw Response object.
      if (error || !data?.ok) {
        let detail = data?.error?.message || 'Approval failed';
        if (error?.context instanceof Response) {
          try {
            const body = await error.context.json();
            detail = body?.error?.message || body?.message || detail;
            console.error('admin-approve-induction body:', body);
          } catch { /* response already consumed or not JSON */ }
        }
        console.error('admin-approve-induction error:', { data, error });
        throw new Error(detail);
      }
      toast({ title: 'Approved', description: `Pass issued to ${reviewingInvite.person_name}` });
      setReviewingInvite(null);
      await loadInvites();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setApproving(false);
    }
  };

  const handleRejectInvite = async () => {
    if (!reviewingInvite || !rejectionReason.trim()) return;
    setRejecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-approve-induction', {
        body: {
          public_reference: reviewingInvite.public_reference,
          approve: false,
          rejection_reason: rejectionReason,
          approved_by: user?.id,
        },
      });
      if (error || !data?.ok) throw new Error(data?.error?.message || error?.message || 'Rejection failed');
      toast({ title: 'Rejected', description: `Induction for ${reviewingInvite.person_name} has been rejected` });
      setReviewingInvite(null);
      await loadInvites();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setRejecting(false);
    }
  };

  const handleCreateSuccess = () => {
    toast({
      title: 'Invite Created',
      description: 'Induction invite sent successfully',
    });
    loadInvites();
  };

  // Filter invites by search term
  const filteredInvites = invites.filter((invite) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      invite.person_name?.toLowerCase().includes(term) ||
      invite.person_email?.toLowerCase().includes(term) ||
      invite.person_company?.toLowerCase().includes(term) ||
      invite.public_reference?.toLowerCase().includes(term) ||
      invite.depot_name?.toLowerCase().includes(term)
    );
  });

  const getStatusBadge = (status) => {
    const variants = {
      invited:                 { cls: 'border border-blue-300 text-blue-700 bg-blue-50',   text: 'Invited' },
      in_progress:             { cls: 'border border-yellow-300 text-yellow-700 bg-yellow-50', text: 'In Progress' },
      submitted:               { cls: 'border border-orange-300 text-orange-700 bg-orange-50', text: 'Needs Review' },
      approved:                { cls: 'border border-green-300 text-green-700 bg-green-50',  text: 'Approved' },
      rejected:                { cls: 'border border-red-300 text-red-700 bg-red-50',       text: 'Rejected' },
      expired:                 { cls: 'border border-gray-300 text-gray-500 bg-gray-50',    text: 'Expired' },
      pending_acknowledgement: { cls: 'border border-amber-300 text-amber-700 bg-amber-50', text: 'Re-confirm' },
    };
    const variant = variants[status] || variants.invited;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${variant.cls}`}>
        {variant.text}
      </span>
    );
  };

  const getPersonTypeBadge = (type) => {
    const variants = {
      employee:   'border border-emerald-300 text-emerald-700 bg-emerald-50',
      contractor: 'border border-blue-300 text-blue-700 bg-blue-50',
      visitor:    'border border-purple-300 text-purple-700 bg-purple-50',
    };
    return variants[type] || 'border border-gray-300 text-gray-600 bg-gray-50';
  };

  const stats = {
    total: invites.length,
    invited: invites.filter((i) => i.status === 'invited').length,
    submitted: invites.filter((i) => i.status === 'submitted').length,
    approved: invites.filter((i) => i.status === 'approved').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading invites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Site Invites</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage contractor/visitor site invites
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Invite
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Invites', value: stats.total,     color: 'text-foreground',   filter: null,        icon: Mail },
          { label: 'Pending',       value: stats.invited,   color: 'text-blue-600',     filter: 'invited',   icon: Clock },
          { label: 'Needs Review',  value: stats.submitted, color: 'text-orange-600',   filter: 'submitted', icon: AlertCircle },
          { label: 'Approved',      value: stats.approved,  color: 'text-green-600',    filter: 'approved',  icon: CheckCircle2 },
        ].map(({ label, value, color, filter, icon: Icon }) => (
          <button
            key={label}
            onClick={() => setStatusFilter(filter)}
            className={`text-left px-4 py-4 rounded-md border transition-colors bg-background hover:bg-muted/40 ${
              statusFilter === filter ? 'border-foreground/30 bg-muted/40' : 'border-border'
            }`}
          >
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">{label}</p>
            <p className={`text-2xl font-semibold ${color}`}>{value}</p>
          </button>
        ))}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, company, or reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        {statusFilter && (
          <Button variant="outline" onClick={() => setStatusFilter(null)}>
            Clear Filter
          </Button>
        )}
        <Button variant="outline" onClick={loadInvites}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Invites List */}
      {filteredInvites.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No invites found</h3>
              <p className="text-muted-foreground mt-2">
                {searchTerm ? 'No results match your search.' : 'Create your first induction invite to get started.'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setIsCreateModalOpen(true)} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Invite
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border border-border overflow-hidden bg-background">
          {/* Table Header */}
          <div className="hidden lg:grid grid-cols-[1fr_90px_1fr_110px_180px_110px_120px_110px] gap-0 px-5 py-2.5 border-b bg-muted/40 text-[10.5px] font-semibold uppercase tracking-widest text-muted-foreground/80">
            <span>Person</span>
            <span>Role</span>
            <span>Contact</span>
            <span>Site</span>
            <span>Reference</span>
            <span>Approved</span>
            <span>Status</span>
            <span className="text-right">Actions</span>
          </div>

          <div className="divide-y divide-border">
            {filteredInvites.map((invite) => {
              const isExpired = new Date(invite.invite_expires_at) < new Date();
              const canResend = ['invited', 'in_progress', 'pending_acknowledgement', 'expired'].includes(invite.status);
              const canDelete = ['invited', 'in_progress', 'pending_acknowledgement', 'expired', 'rejected', 'submitted', 'approved'].includes(invite.status);
              const createdDate = new Date(invite.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
              const expiryDate = invite.invite_expires_at
                ? new Date(invite.invite_expires_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })
                : null;
              const approvedDate = invite.approved_at
                ? new Date(invite.approved_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })
                : null;

              return (
                <React.Fragment key={invite.public_reference}>
                  {/* ── Desktop row ── */}
                  <div
                    className="hidden lg:grid grid-cols-[1fr_90px_1fr_110px_180px_110px_120px_110px] gap-0 px-5 py-3.5 items-center hover:bg-muted/20 transition-colors"
                  >
                    {/* Person */}
                    <div className="pr-3 min-w-0">
                      <span className="font-medium text-sm truncate block">{invite.person_name}</span>
                    </div>

                    {/* Role */}
                    <div className="pr-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${getPersonTypeBadge(invite.person_type)}`}>
                        {invite.person_type}
                      </span>
                    </div>

                    {/* Contact */}
                    <div className="pr-4 min-w-0">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0">
                        <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                        <span className="truncate">{invite.person_email}</span>
                        {invite.person_company && (
                          <><span className="text-muted-foreground/30 shrink-0">·</span>
                          <span className="text-muted-foreground/70 truncate text-xs">{invite.person_company}</span></>
                        )}
                      </div>
                      {invite.nature_of_work && (
                        <div className="text-xs text-muted-foreground/70 truncate mt-0.5 pl-5">{invite.nature_of_work}</div>
                      )}
                    </div>

                    {/* Site */}
                    <div className="pr-3 min-w-0">
                      <span className="text-xs text-muted-foreground truncate block">{invite.depot_name || '—'}</span>
                    </div>

                    {/* Reference + dates */}
                    <div className="pr-4">
                      <div className="font-mono text-xs font-semibold text-foreground whitespace-nowrap">{invite.public_reference}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 whitespace-nowrap">
                        {createdDate}
                        {expiryDate && (
                          <span className={`ml-2 ${isExpired ? 'text-red-500 font-medium' : ''}`}>
                            · Exp. {expiryDate}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Approved date */}
                    <div className="pr-3">
                      {approvedDate ? (
                        <span className="text-xs text-green-600 font-medium whitespace-nowrap">✓ {approvedDate}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground/40">—</span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      {getStatusBadge(invite.status)}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1.5 justify-end">
                      {invite.status === 'submitted' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 border-orange-400 text-orange-600 hover:bg-orange-50 font-medium shrink-0"
                          onClick={() => openReview(invite)}
                          title="Review induction"
                        >
                          <Shield className="h-3.5 w-3.5 mr-1.5" />
                          Review
                        </Button>
                      )}
                      {canResend && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResend(invite.public_reference)}
                          disabled={resendingRef === invite.public_reference}
                          title="Resend invite email"
                          className="h-8 w-8 p-0"
                        >
                          {resendingRef === invite.public_reference ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent" />
                          ) : (
                            <Send className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(invite)}
                          disabled={deletingRef === invite.public_reference}
                          title="Delete invite"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          {deletingRef === invite.public_reference ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-500 border-t-transparent" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* ── Mobile / tablet card ── */}
                  <div className="lg:hidden px-4 py-3.5 space-y-2.5 hover:bg-muted/20 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="font-semibold text-sm">{invite.person_name}</span>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${getPersonTypeBadge(invite.person_type)}`}>
                            {invite.person_type}
                          </span>
                          {invite.depot_name && (
                            <span className="text-xs text-muted-foreground">{invite.depot_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0">{getStatusBadge(invite.status)}</div>
                    </div>

                    <div className="text-xs text-muted-foreground space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate">{invite.person_email}</span>
                        {invite.person_company && <span className="text-muted-foreground/60">· {invite.person_company}</span>}
                      </div>
                      {invite.nature_of_work && (
                        <div className="pl-4 text-muted-foreground/70">{invite.nature_of_work}</div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-xs">
                        <span className="font-mono font-semibold text-foreground">{invite.public_reference}</span>
                        <span className="text-muted-foreground ml-2">{createdDate}</span>
                        {expiryDate && (
                          <span className={`ml-1 ${isExpired ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>· Exp. {expiryDate}</span>
                        )}
                        {approvedDate && (
                          <span className="ml-1 text-green-600 font-medium">· ✓ {approvedDate}</span>
                        )}
                      </div>
                      <div className="flex gap-1.5">
                        {invite.status === 'submitted' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 border-orange-400 text-orange-600 hover:bg-orange-50 font-medium"
                            onClick={() => openReview(invite)}
                          >
                            <Shield className="h-3.5 w-3.5 mr-1" />Review
                          </Button>
                        )}
                        {canResend && (
                          <Button variant="outline" size="sm" onClick={() => handleResend(invite.public_reference)}
                            disabled={resendingRef === invite.public_reference} className="h-7 w-7 p-0">
                            {resendingRef === invite.public_reference
                              ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent" />
                              : <Send className="h-3 w-3" />}
                          </Button>
                        )}
                        {canDelete && (
                          <Button variant="outline" size="sm" onClick={() => handleDelete(invite)}
                            disabled={deletingRef === invite.public_reference}
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50">
                            {deletingRef === invite.public_reference
                              ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-500 border-t-transparent" />
                              : <Trash2 className="h-3 w-3" />}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewingInvite} onOpenChange={(open) => { if (!open) setReviewingInvite(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-500" />
              Review Induction
            </DialogTitle>
          </DialogHeader>

          {reviewLoading ? (
            <div className="py-8 flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div>
          ) : reviewingInvite && (
            <div className="space-y-4">
              {/* Person details */}
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-base">{reviewingInvite.person_name}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${getPersonTypeBadge(reviewingInvite.person_type)}`}>
                    {reviewingInvite.person_type}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Email</p>
                    <p>{reviewingInvite.person_email}</p>
                  </div>
                  {reviewingInvite.person_company && (
                    <div>
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Company</p>
                      <p>{reviewingInvite.person_company}</p>
                    </div>
                  )}
                  {reviewingInvite.depot_name && (
                    <div>
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Site</p>
                      <p>{reviewingInvite.depot_name}</p>
                    </div>
                  )}
                  {(reviewSubmission?.nature_of_work || reviewingInvite.nature_of_work) && (
                    <div className="col-span-2">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Nature of Work</p>
                      <p>{reviewSubmission?.nature_of_work || reviewingInvite.nature_of_work}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Reference</p>
                    <p className="font-mono font-semibold">{reviewingInvite.public_reference}</p>
                  </div>
                  {reviewingInvite.submitted_at && (
                    <div>
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Submitted</p>
                      <p>{new Date(reviewingInvite.submitted_at).toLocaleString('en-GB')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Brief answers */}
              {reviewSubmission?.answers && Object.keys(reviewSubmission.answers).length > 0 && (
                <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Induction Responses</p>
                  <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                    {Object.entries(reviewSubmission.answers).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="font-medium text-foreground">{key}:</span>{' '}
                        <span className="text-muted-foreground">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reject reason input */}
              {showRejectForm && (
                <div className="space-y-1.5">
                  <Label htmlFor="rejection_reason" className="text-sm font-medium">
                    Rejection Reason <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="rejection_reason"
                    placeholder="Explain why this induction is being rejected..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {!showRejectForm ? (
              <>
                <Button variant="outline" onClick={() => setReviewingInvite(null)}>Cancel</Button>
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => setShowRejectForm(true)}
                >
                  <XCircle className="h-4 w-4 mr-1.5" />Reject
                </Button>
                <Button
                  onClick={handleApproveInvite}
                  disabled={approving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {approving ? (
                    <><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent mr-1.5" />Approving...</>
                  ) : (
                    <><CheckCircle2 className="h-4 w-4 mr-1.5" />Approve &amp; Issue Pass</>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}>Back</Button>
                <Button
                  variant="destructive"
                  onClick={handleRejectInvite}
                  disabled={rejecting || !rejectionReason.trim()}
                >
                  {rejecting ? (
                    <><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent mr-1.5" />Rejecting...</>
                  ) : 'Confirm Rejection'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invite Modal */}
      <CreateInviteModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
        depots={depots}
        defaultDepotId={user?.depot_id || null}
        currentUser={user}
      />
    </div>
  );
};

export default InductionInviteManager;
